import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Reusable hook for pagination with URL sync
 * @param {Object} options - Configuration options
 * @param {number} options.defaultPage - Default page number (0-based)
 * @param {number} options.defaultPageSize - Default page size
 * @param {Array<number>} options.pageSizeOptions - Available page size options
 * @returns {Object} Pagination state and handlers
 */
export const usePaginationWithUrl = ({
  defaultPage = 0,
  defaultPageSize = 10,
  pageSizeOptions = [10, 20, 50, 100]
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize from URL or use defaults (URL uses 1-based, internal uses 0-based)
  const [page, setPage] = useState(() => {
    const urlPage = searchParams.get('page');
    return urlPage ? parseInt(urlPage, 10) - 1 : defaultPage;
  });
  
  const [pageSize, setPageSize] = useState(() => {
    const urlPageSize = searchParams.get('pageSize');
    return urlPageSize ? parseInt(urlPageSize, 10) : defaultPageSize;
  });
  
  // Update URL when pagination changes (convert 0-based to 1-based for URL)
  const updateUrl = useCallback((newPage, newPageSize) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', (newPage + 1).toString()); // Convert to 1-based for URL
    params.set('pageSize', newPageSize.toString());
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);
  
  // Initialize URL params on mount if not present
  useEffect(() => {
    const urlPage = searchParams.get('page');
    const urlPageSize = searchParams.get('pageSize');
    
    if (!urlPage || !urlPageSize) {
      updateUrl(page, pageSize);
    }
  }, []); // Run only once on mount
  
  // Handle page change
  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
    updateUrl(newPage, pageSize);
  }, [pageSize, updateUrl]);
  
  // Handle page size change
  const handlePageSizeChange = useCallback((event) => {
    const newPageSize = parseInt(event.target.value, 10);
    setPageSize(newPageSize);
    setPage(0); // Reset to first page
    updateUrl(0, newPageSize);
  }, [updateUrl]);
  
  // Reset to first page (useful after filters change)
  const resetPage = useCallback(() => {
    setPage(0);
    updateUrl(0, pageSize);
  }, [pageSize, updateUrl]);
  
  return {
    page,
    pageSize,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
    resetPage,
    setPage: (newPage) => {
      setPage(newPage);
      updateUrl(newPage, pageSize);
    }
  };
};
